import React from 'react';
import { cn } from '../../lib/utils';
import { DayCell } from './approvals.utils';

interface MonthStripProps {
  cells: DayCell[];
  /** Off days (holidays, company closings) render blank like weekends, so weeks read as clusters. */
  isOffDay?: (dateKey: string) => boolean;
  /** Days flagged by a consistency check get a red base line (or a red gap line when empty). */
  isIssueDay?: (dateKey: string) => boolean;
  cellTitle: (cell: DayCell) => string;
  label: string;
  className?: string;
}

/** 12 hours fill the bar; a regular 8h day sits at two thirds, so overtime visibly rises above the line. */
const BAR_MAX_HOURS = 12;
const BAR_MAX_PX = 16;

const pxForHours = (hours: number, minimum: number) =>
  hours <= 0 ? 0 : Math.max(minimum, Math.round((Math.min(hours, BAR_MAX_HOURS) / BAR_MAX_HOURS) * BAR_MAX_PX));

const stateClass: Record<DayCell['state'], string> = {
  none: '',
  pending: 'bg-amber-400 dark:bg-amber-500',
  approved: 'bg-green-500 dark:bg-green-500',
  mixed: 'bg-amber-400 dark:bg-amber-500',
};

/**
 * One month in ~180px: a cell per day, bar height = hours, colour = approval state,
 * orange cap = overtime, blue dot = on-call, blank = weekend/holiday, hairline = weekday without entry,
 * red base line = day flagged by a consistency check.
 */
export function MonthStrip({ cells, isOffDay, isIssueDay, cellTitle, label, className }: MonthStripProps) {
  return (
    <div role="img" aria-label={label} className={cn('flex h-5 items-end gap-px', className)}>
      {cells.map((cell) => {
        const hasEntry = cell.entries > 0;
        const offDay = cell.isWeekend || Boolean(isOffDay?.(cell.dateKey));
        const issueDay = Boolean(isIssueDay?.(cell.dateKey));
        const workedPx = pxForHours(cell.workedHours, 3);
        const overtimePx = pxForHours(cell.overtimeHours, 2);

        return (
          <span
            key={cell.day}
            title={cellTitle(cell)}
            className="relative flex h-full w-[5px] shrink-0 flex-col justify-end"
          >
            {cell.onCall && (
              <span className="absolute left-1/2 top-0 h-1 w-1 -translate-x-1/2 rounded-full bg-blue-500" />
            )}
            {hasEntry ? (
              <>
                {overtimePx > 0 && (
                  <span className="block w-full rounded-t-[1px] bg-orange-500" style={{ height: overtimePx }} />
                )}
                <span
                  className={cn('block w-full', overtimePx === 0 && 'rounded-t-[1px]', stateClass[cell.state])}
                  style={{ height: workedPx || (cell.onCall || cell.permitHours > 0 ? 3 : 0) }}
                />
                {issueDay && <span data-testid="issue-mark" className="block h-[2px] w-full bg-red-500" />}
              </>
            ) : issueDay ? (
              <span data-testid="issue-mark" className="block h-[2px] w-full bg-red-500" />
            ) : (
              !offDay && <span className="block h-px w-full bg-foreground/30" />
            )}
          </span>
        );
      })}
    </div>
  );
}

export default MonthStrip;
